import { PlanLimit, type PlanLimitReachedData, type PlanQuotaMeta } from '@fluenceur/types';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Cache key of the brand's Discovery search counter as the session's searches
 * state it: every search answers with it (`meta.quota`) and the quota refusal
 * carries it too. Before the first search, `useDiscoveryQuota` reads it from
 * the features the dashboard loads.
 */
export const discoveryQuotaQueryKey = (brandId: string) => ['discovery-quota', brandId] as const;

/** Records the counter a search or a quota refusal stated for this brand. */
export function rememberDiscoveryQuota(
  queryClient: QueryClient,
  brandId: string,
  quota: PlanQuotaMeta | undefined
): void {
  if (!brandId || !quota) return;
  // Kept for the session even while no counter is on screen: nothing could
  // fetch it back.
  queryClient.setQueryDefaults(['discovery-quota'], { gcTime: Infinity });
  queryClient.setQueryData(discoveryQuotaQueryKey(brandId), quota);
}

/** The counter a Discovery quota refusal states: every search of the period used. */
export function quotaFromRefusal(refusal: PlanLimitReachedData | null): PlanQuotaMeta | undefined {
  if (refusal?.limit !== PlanLimit.DISCOVERY_SEARCHES || refusal.max === undefined)
    return undefined;
  return {
    used: refusal.used ?? refusal.max,
    limit: refusal.max,
    resetsAt: refusal.resetsAt ?? null,
  };
}
